import axios from "axios";
import { authConfig } from "../utils/apiUtils";

/**
 * API client for the country-configurable custom fields engine
 *
 * These endpoints live under `/v1/countries/{countryId}/...`, so the full URL is
 * built against REACT_APP_API_ENDPOINT and the caller's bearer token is passed
 * explicitly via `authConfig` - the same pattern used elsewhere for
 * `/v1/countries` calls.
 */

export type CustomFieldType =
  | "text"
  | "number"
  | "date"
  | "dropdown"
  | "checkbox";

export type CustomFieldStatus = "active" | "hidden" | "archived";

/**
 * Field types a uniqueness constraint can be applied to. A date, a checkbox or
 * a value from a short dropdown list is shared by design, so constraining those
 * would make the field unusable - the API rejects the flag on them.
 */
export const UNIQUE_CAPABLE_FIELD_TYPES: CustomFieldType[] = ["text", "number"];

export const supportsUniqueness = (fieldType: CustomFieldType): boolean =>
  UNIQUE_CAPABLE_FIELD_TYPES.indexOf(fieldType) !== -1;

/**
 * Company-type targeting for a custom field. Defaults to "all" so a new field is
 * never silently narrowed to one audience.
 */
export type FieldAppliesTo = "all" | "kone" | "subcon";

export const FIELD_APPLIES_TO_VALUES: FieldAppliesTo[] = [
  "all",
  "kone",
  "subcon",
];

export interface BusinessTypeOption {
  businessTypeId: number;
  businessTypeName: string;
}

export interface BaseFieldDefinition {
  fieldKey: string;
  labelKey: string;
  storageColumn: string;
  dataType: CustomFieldType;
  configurable: boolean;
  defaultVisible: boolean;
  defaultMandatory: boolean;
  displayOrder: number;
}

export interface CustomFieldDefinition {
  id?: number;
  countryId?: number;
  columnName: string;
  displayName: string;
  fieldType: CustomFieldType;
  isMandatory: boolean;
  charLimit?: number | null;
  fieldOptions?: string[] | null;
  displayOrder: number;
  status: CustomFieldStatus;
  /**
   * No two users in this country may hold the same value. Only offered for
   * "text" and "number" - see UNIQUE_CAPABLE_FIELD_TYPES.
   */
  isUnique?: boolean;
  /** Company-type targeting. Defaults to "all". */
  appliesTo?: FieldAppliesTo;
  /** Targeted business types. EMPTY means "applies to every business type". */
  businessTypeIds?: number[];
}

export interface DefaultFieldOverride {
  countryId?: number;
  fieldKey: string;
  isVisible: boolean;
  isMandatory: boolean;
  displayOrder: number;
}

export interface MergedFieldSchemaItem {
  key: string;
  /** Base fields only: the name the admin create/edit forms know it by. */
  formKey?: string;
  labelKey?: string;
  displayName?: string;
  source: "base" | "custom";
  fieldType: CustomFieldType;
  storageColumn?: string;
  isVisible: boolean;
  isMandatory: boolean;
  isConfigurable: boolean;
  charLimit?: number | null;
  fieldOptions?: string[] | null;
  displayOrder: number;
  /** Custom text/number fields only: no two users may share this value. */
  isUnique?: boolean;
  /**
   * Audience targeting. Present when the schema is fetched without a
   * `companyType` filter, which is what a consumer showing several audiences at
   * once (the bulk grid) does so it can filter per row.
   */
  appliesTo?: FieldAppliesTo;
  /** Empty means the field applies to every business type. */
  businessTypeIds?: number[];
}

export interface OnboardConfig {
  baseFieldCatalog: BaseFieldDefinition[];
  baseFieldOverrides: DefaultFieldOverride[];
  customFields: CustomFieldDefinition[];
  /** Catalog for the "applies to" business-type picker. */
  businessTypes: BusinessTypeOption[];
}

export interface OnboardConfigPayload {
  baseFieldOverrides?: Array<{
    fieldKey: string;
    isVisible?: boolean;
    isMandatory?: boolean;
    displayOrder?: number;
  }>;
  customFields?: Array<{
    columnName: string;
    displayName: string;
    fieldType: CustomFieldType;
    isMandatory?: boolean;
    charLimit?: number | null;
    fieldOptions?: string[] | null;
    displayOrder?: number;
    status?: CustomFieldStatus;
    isUnique?: boolean;
    appliesTo?: FieldAppliesTo;
    businessTypeIds?: number[];
  }>;
}

/** A country as returned by GET /v1/countries. */
export interface CountryOption {
  country_id: number;
  name: string;
  status?: string;
  country_code?: string;
  phone_code?: string;
  frontline_id?: number;
}

const baseUrl = () => `${process.env.REACT_APP_API_ENDPOINT}/v1/countries`;

/** GET the full onboarding config (base catalog + overrides + custom fields). */
export const getOnboardConfig = async (
  countryId: number | string,
  token: string,
): Promise<OnboardConfig> => {
  const response = await axios.get(
    `${baseUrl()}/${countryId}/onboard-config`,
    authConfig(token),
  );
  return response.data;
};

/**
 * POST the onboarding config (Save & Onboard). Returns the merged schema plus
 * `archivedFields` - custom fields the API archived because they were absent
 * from this payload. Surface those to the admin: the payload is treated as the
 * complete desired state, so an omission removes a field from the form.
 */
export const saveOnboardConfig = async (
  countryId: number | string,
  payload: OnboardConfigPayload,
  token: string,
): Promise<{
  message: string;
  schema: MergedFieldSchemaItem[];
  archivedFields: string[];
}> => {
  const response = await axios.post(
    `${baseUrl()}/${countryId}/onboard-config`,
    payload,
    authConfig(token),
  );
  return response.data;
};

/** What a country's config says about one of the built-in user fields. */
export interface BaseFieldRules {
  isVisible: boolean;
  isMandatory: boolean;
}

/**
 * Pull the base-field rules out of a merged schema, keyed by `formKey` - the
 * name the admin forms use for the field in their own state ("contactNumber",
 * "koneEmployeeId", ...).
 *
 * The create and edit forms render the built-in fields natively rather than
 * from the schema, so they need this to honour a country's visibility and
 * mandatory overrides. Keying on `formKey`, which the API supplies from the
 * base field catalog, is what lets them do that without holding their own list
 * of configurable fields: add a field to the catalog and the forms pick it up.
 *
 * Custom fields are excluded - those are rendered from the schema directly.
 */
export const baseFieldRulesFrom = (
  schema: MergedFieldSchemaItem[],
): Record<string, BaseFieldRules> => {
  const rules: Record<string, BaseFieldRules> = {};
  (schema || [])
    .filter((item) => item.source === "base" && item.formKey)
    .forEach((item) => {
      rules[item.formKey as string] = {
        isVisible: item.isVisible !== false,
        isMandatory: !!item.isMandatory,
      };
    });
  return rules;
};

/**
 * Who a field schema is being resolved for.
 *
 * Mirrors the server's `FieldAudience`: a custom field applies only when BOTH
 * its company-type targeting (`appliesTo`) and its business-type targeting
 * match. An empty `businessTypeIds` means "not known", and the server treats
 * that as unrestricted rather than hiding fields on missing context.
 */
export interface FieldSchemaAudience {
  companyType: "kone" | "subcon";
  businessTypeIds?: number[];
}

/**
 * GET the merged field schema that drives the dynamic user form + passport.
 *
 * Pass `audience` from any screen that is working on ONE user, so only the
 * fields targeted at that user are returned - this is what keeps a
 * subcontractor-only field off a KONE internal form. Omit it only where several
 * audiences are on screen at once (the country field builder, and the bulk grid,
 * which fetches once per country and filters per row itself).
 */
export const getFieldSchema = async (
  countryId: number | string,
  token: string,
  audience?: FieldSchemaAudience,
): Promise<MergedFieldSchemaItem[]> => {
  const params: string[] = [];
  if (audience) {
    params.push(`companyType=${encodeURIComponent(audience.companyType)}`);
    const businessTypeIds = (audience.businessTypeIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
    if (businessTypeIds.length)
      params.push(`businessTypeIds=${businessTypeIds.join(",")}`);
  }
  const query = params.length ? `?${params.join("&")}` : "";
  const response = await axios.get(
    `${baseUrl()}/${countryId}/field-schema${query}`,
    authConfig(token),
  );
  return response.data;
};
